/**
 * Self-Reflection Panel
 *
 * Phase B1 of the Employee Journey Rework (May 2026).
 *
 * The team member's high-level "My Headlines" reflection at check-in prep
 * — captured BEFORE drilling into the per-KPA reflections. Two free-text
 * fields: biggest successes / biggest challenges in the impact period
 * under review.
 *
 * Earlier drafts of the plan included Coaching points / My Impact /
 * Human Superpower / Career Experience / My Leader sections here too.
 * HR feedback (2026-05-08) moved Coaching / Impact / Superpower to My
 * Personalised Growth (B2/B3) and Career / Leader to the Feedback system,
 * so this panel deliberately stays focused on Headlines only.
 *
 * Storage: session-scoped via `reviewService.{get,upsert}SelfReflection`.
 * Backend enforces that only the team member can author their own
 * reflection and that the session is still pending/scheduled.
 */

import { useEffect, useState, useCallback, useRef } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { Sparkles, CheckCircle, Leaf, Loader2 } from 'lucide-react'
import { reviewService } from '@/services/api/reviewService'
import toast from 'react-hot-toast'

const MAX_LEN = 1500

interface SelfReflectionPanelProps {
  sessionId: string
  /**
   * When true (manager view), the panel renders read-only — managers see
   * what the team member wrote but cannot edit it. The team member is the
   * sole author. We also pass canEdit=false when the session has moved
   * past prep (in-progress / completed).
   */
  canEdit: boolean
  /** Optional display name to personalise the empty-state copy in manager view. */
  memberName?: string
}

export function SelfReflectionPanel({ sessionId, canEdit, memberName }: SelfReflectionPanelProps) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [successes, setSuccesses] = useState('')
  const [challenges, setChallenges] = useState('')
  const [hasReflection, setHasReflection] = useState(false)
  const [updatedAt, setUpdatedAt] = useState<string | null>(null)

  // #19: auto-save (like My Headlines) — track last-persisted values so the
  // debounced save only fires on real edits, and debounce per field.
  const persisted = useRef({ successes: '', challenges: '' })
  const successesTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const challengesTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const reflection = await reviewService.getSelfReflection(sessionId)
        if (cancelled) return
        if (reflection) {
          const s = reflection.headlines_successes ?? ''
          const c = reflection.headlines_challenges ?? ''
          setSuccesses(s)
          setChallenges(c)
          persisted.current = { successes: s, challenges: c }
          setHasReflection(true)
          setUpdatedAt(reflection.updated_at)
        } else {
          setSuccesses('')
          setChallenges('')
          persisted.current = { successes: '', challenges: '' }
          setHasReflection(false)
          setUpdatedAt(null)
        }
      } catch (err) {
        console.error('Failed to load self-reflection:', err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
      if (successesTimer.current) clearTimeout(successesTimer.current)
      if (challengesTimer.current) clearTimeout(challengesTimer.current)
    }
  }, [sessionId])

  const persist = useCallback(
    async (field: 'headlines_successes' | 'headlines_challenges', value: string) => {
      if (!canEdit) return
      setSaving(true)
      try {
        const result = await reviewService.upsertSelfReflection(sessionId, {
          [field]: value,
        })
        if (field === 'headlines_successes') persisted.current.successes = value
        else persisted.current.challenges = value
        setUpdatedAt(result.updated_at)
        setHasReflection(true)
      } catch (err: any) {
        console.error('Failed to save self-reflection:', err)
        toast.error(err?.message || 'Could not save your reflection — try again.')
      } finally {
        setSaving(false)
      }
    },
    [canEdit, sessionId],
  )

  const onSuccessesChange = (value: string) => {
    const v = value.slice(0, MAX_LEN)
    setSuccesses(v)
    if (successesTimer.current) clearTimeout(successesTimer.current)
    successesTimer.current = setTimeout(() => {
      if (v !== persisted.current.successes) void persist('headlines_successes', v)
    }, 900)
  }

  const onChallengesChange = (value: string) => {
    const v = value.slice(0, MAX_LEN)
    setChallenges(v)
    if (challengesTimer.current) clearTimeout(challengesTimer.current)
    challengesTimer.current = setTimeout(() => {
      if (v !== persisted.current.challenges) void persist('headlines_challenges', v)
    }, 900)
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 flex items-center justify-center text-gray-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          Loading reflection…
        </CardContent>
      </Card>
    )
  }

  // Manager / read-only view with empty reflection — show an explanatory
  // empty state rather than the editor.
  if (!canEdit && !hasReflection) {
    return (
      <Card className="border-l-4 border-l-indigo-400 bg-indigo-50/40">
        <CardContent className="py-8 text-center">
          <Sparkles className="h-8 w-8 mx-auto mb-3 text-indigo-500" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No reflection yet</h3>
          <p className="text-sm text-gray-600">
            {memberName
              ? `${memberName} hasn't shared a self-reflection for this check-in yet.`
              : 'The team member hasn\'t shared a self-reflection for this check-in yet.'}
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="border-l-4 border-l-stone-300 bg-stone-50/60">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-xl flex items-center gap-2 font-serif text-stone-800">
              <Sparkles className="h-5 w-5 text-stone-500" />
              {canEdit ? 'My headlines' : `${memberName ? `${memberName}'s` : 'Their'} headlines`}
            </CardTitle>
            <p className="text-sm text-stone-600 mt-1">
              {canEdit
                ? 'A quick, high-level take on the period before drilling into each KPA. Free-form — write as much or as little as feels right. Private to you, saved as you go.'
                : `${memberName ? `${memberName}'s` : 'The team member\'s'} high-level take on the period.`}
            </p>
          </div>
          {/* #19: auto-save indicator replaces the manual Save button — quiet,
              non-RAG "Saved {time}" / "Saving…" cue. */}
          {canEdit && (
            <span className="text-xs text-stone-500 whitespace-nowrap flex items-center gap-1.5 mt-1">
              {saving ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Saving…
                </>
              ) : updatedAt ? (
                <>
                  <CheckCircle className="h-3 w-3 text-stone-400" />
                  Saved {new Date(updatedAt).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit', hour12: true })}
                </>
              ) : null}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Successes */}
        <div>
          <label className="text-sm font-semibold text-stone-800 flex items-center gap-2 mb-2">
            <CheckCircle className="h-4 w-4 text-stone-500" />
            Biggest successes in the period
          </label>
          {canEdit ? (
            <>
              <Textarea
                value={successes}
                onChange={(e) => onSuccessesChange(e.target.value)}
                rows={5}
                placeholder="What are you most proud of? What landed well? What did you contribute that you want to make sure your people leader knows about?"
                className="resize-y bg-white border-stone-200 focus-visible:ring-stone-300"
              />
              <div className="text-xs text-stone-400 mt-1 text-right">
                {successes.length} / {MAX_LEN}
              </div>
            </>
          ) : (
            <div className="rounded-md border border-stone-200 bg-white p-3 text-sm text-stone-800 whitespace-pre-wrap min-h-[72px]">
              {successes || <span className="text-stone-400 italic">No successes captured.</span>}
            </div>
          )}
        </div>

        {/* Challenges — #19: neutral leaf marker, not an orange ⚠. Challenges
            are part of an honest reflection, not warnings. */}
        <div>
          <label className="text-sm font-semibold text-stone-800 flex items-center gap-2 mb-2">
            <Leaf className="h-4 w-4 text-stone-500" />
            Biggest challenges in the period
          </label>
          {canEdit ? (
            <>
              <Textarea
                value={challenges}
                onChange={(e) => onChallengesChange(e.target.value)}
                rows={5}
                placeholder="What got in the way? What would you do differently? What support might have made the difference?"
                className="resize-y bg-white border-stone-200 focus-visible:ring-stone-300"
              />
              <div className="text-xs text-stone-400 mt-1 text-right">
                {challenges.length} / {MAX_LEN}
              </div>
            </>
          ) : (
            <div className="rounded-md border border-stone-200 bg-white p-3 text-sm text-stone-800 whitespace-pre-wrap min-h-[72px]">
              {challenges || <span className="text-stone-400 italic">No challenges captured.</span>}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

export default SelfReflectionPanel
